<template>
  <div class="index">
    <transition
      enter-active-class="animate__animated animate__fadeIn fast"
      leave-active-class="animate__animated animate__fadeOut fast"
    >
      <create-proposal-modal
        v-if="showModal"
        @close="showModal = false"
      />
    </transition>

    <gov-header>
      <div class="columns is-centered">
        <div class="column is-three-fifths-desktop is-four-fifths-tablet">
          <div class="box">
            <nav class="level is-mobile info">
              <div class="level-item has-text-centered">
                <div>
                  <p class="heading">Proposals</p>
                  <p v-if="$store.daoParameters !== null" class="title">
                    <router-link :to="{name: 'Proposals'}" class="has-text-primary" v-if="$store.daoParameters.nextProposalId.isGreaterThan(0)">{{ $store.daoParameters.nextProposalId }}</router-link>
                    <span class="has-text-dark" v-else>0</span>
                  </p>
                  <div v-else class="loader-wrapper is-paddingless"><div class="loader is-primary is-medium"></div></div>
                </div>
              </div>
              <div class="level-item has-text-centered">
                <div>
                  <p class="heading">Current Block</p>
                  <p v-if="$store.currentBlockHeight !== null" class="title has-text-primary">
                    <a target="_blank" rel="noopener" :href="`${$store.tzstatsURL}/${$store.currentBlockHeight}`">
                      {{ $store.currentBlockHeight.toLocaleString() }}
                    </a>
                  </p>
                  <div v-else class="loader-wrapper is-paddingless"><div class="loader is-primary is-medium"></div></div>
                </div>
              </div>
              <div class="level-item has-text-centered">
                <div>
                  <p class="heading propsal-escrow">
                    Proposal Escrow
                    <popover>
                      <span slot="popup-content">
                        <strong v-if="$store.daoParameters !== null">
                          Escrow is the amount of kDAO you have to lock up to propose a change.

                          The proposal must hit a minimum amount of yay votes (currently {{ $store.daoParameters.governanceParameters.minYayVotesPercentForEscrowReturn }}%) or the kDAO submitted isn't returned and instead goes into a community fund to be distributed in the future.
                        </strong>
                      </span>
                      <span>(<a class="has-text-primary has-text-weight-bold">?</a>)</span>
                    </popover>
                  </p>
                  <p v-if="$store.daoParameters !== null" class="title has-text-dark is-flex is-align-items-flex-end is-justify-content-center not-linked">
                    <span>
                      {{ $store.daoParameters.governanceParameters.escrowAmount.dividedBy(Math.pow(10, 18)).toNumber() }}
                    </span>
                    <span class="heading">kDAO</span>
                  </p>
                  <div v-else class="loader-wrapper is-paddingless"><div class="loader is-primary is-medium"></div></div>
                </div>
              </div>
            </nav>
          </div>
        </div>
      </div>
    </gov-header>

    <section v-if="
                $store.daoParameters !== null &&
                $store.daoParameters.timelockItem !== null" class="section is-paddingless proposals-and-timelocks">
      <div class="columns is-centered">
        <div class="column is-three-fifths-desktop is-four-fifths-tablet">
          <div class="box info-box content">
            <nav class="level">
              <!-- Left side -->
              <div class="level-left">
                <div class="level-item">
                  <h1 class="has-text-weight-medium is-marginless">Timelock</h1>
                </div>
              </div>

              <!-- Right side -->
              <div class="level-right">
                <div v-if="$store.timelockItemOutcome === null">
                  <div class="loader is-medium is-primary right-spaced"></div>
                </div>
                <div v-else class="level-item">
                  <div class="columns is-centered">
                    <div class="column">
                      <div class="field is-grouped is-grouped-multiline">
                        <div class="control">
                          <div class="tags has-addons">
                            <span class="tag is-dark">Executable</span>
                            <span v-if="timelockExecutableIn === 'Now'" class="tag is-primary">✔︎</span>
                            <span v-else class="tag is-danger is-light">❌</span>
                          </div>
                        </div>
                        <div class="control">
                          <div class="tags has-addons">
                            <span class="tag is-dark">Cancellable</span>
                            <span v-if="timelockCancellableIn === 'Now'" class="tag is-primary">✔︎</span>
                            <span v-else class="tag is-danger is-light">️❌</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </nav>

            <hr>

            <h3 class="proposal-title has-text-weight-medium">
              {{ $store.daoParameters.timelockItem.proposal.title }}
            </h3>

            <hr>

            <div class="loader-wrapper" v-if="$store.timelockItemOutcome === null">
              <div class="loader is-large is-primary"></div>
            </div>
            <timelock-countdowns
              v-else
              :timelock-outcome="$store.timelockItemOutcome"
              :timelock-item="$store.daoParameters.timelockItem" />

            <div class="buttons is-right">
              <router-link :to="{'name': 'Timelock'}" class="button is-primary">View Timelock Proposal</router-link>
            </div>
          </div>
        </div>
      </div>
    </section>
    <section class="section is-paddingless proposals-and-timelocks">
      <div class="columns is-centered">
        <div class="column is-three-fifths-desktop is-four-fifths-tablet">
          <div class="box info-box loading" v-if="$store.daoParameters === null || ($store.wallet !== null && $store.kDAOCheckpointCount === null)">
            <div class="loader-wrapper">
              <div class="loader is-large is-primary"></div>
            </div>
          </div>
          <div v-else>
            <proposal
              v-if="$store.daoParameters.poll !== null"
              :poll-data="$store.daoParameters.poll"
              :prop-number="$store.daoParameters.nextProposalId"
            />
            <div v-else class="box info-box content">
              <h1 class="title is-4">No Current Proposals</h1>
              <p>There are no current proposals in the Kolibri Governance DAO.</p>
              <p>Once you've connected your wallet, click "Create Proposal" below to submit a new proposal to the DAO.</p>
              <div class="buttons is-centered">
                <button :disabled="$store.wallet === null" @click="showModal = true" class="button is-primary is-medium">Create Proposal</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import moment from "moment";
import Popover from "@/components/Popover";
import CreateProposalModal from "@/components/CreateProposalModal";
import Proposal from "@/components/Proposal";
import GovHeader from "@/components/GovHeader";
import TimelockCountdowns from "@/components/TimelockCountdowns";
export default {
  name: 'Index',
  components: {TimelockCountdowns, GovHeader, Proposal, CreateProposalModal, Popover},
  async mounted(){
    setInterval(() => {
      this.tick = new Date()
    }, 1000)
  },
  data(){
    return {
      tick: new Date(),
      networkLoading: false,
      showModal: false,
    }
  },
  methods:{},
  computed: {
    blocksUntilTimelockExecutable(){
      return this.$store.daoParameters.timelockItem.endBlock.minus(this.$store.currentBlockHeight)
    },
    blocksUntilTimelockCancellable(){
      return this.$store.daoParameters.timelockItem.cancelBlock.minus(this.$store.currentBlockHeight)
    },
    timelockIsExecutable(){
      return this.blocksUntilTimelockExecutable.isLessThanOrEqualTo(0) && this.$store.daoParameters.timelockItem.author === this.$store.walletAddress
    },
    timelockExecutableIn(){
      const durationSeconds = this.blocksUntilTimelockExecutable * this.$store.blockTime
      if (durationSeconds > 0){
        return moment.duration(durationSeconds).humanize(true)
      } else {
        return "Now"
      }
    },
    timelockIsCancellable(){
      return this.blocksUntilTimelockCancellable.isLessThanOrEqualTo(0)
    },
    timelockCancellableIn(){
      const durationSeconds = this.blocksUntilTimelockCancellable * this.$store.blockTime
      if (durationSeconds > 0){
        return moment.duration(durationSeconds).humanize(true)
      } else {
        return "Now"
      }
    },
  }
}
</script>

<style lang="scss">
@import '../assets/sass/globals';
.index, .all-proposals, .proposal-detail{
  .info{
    overflow-y: hidden;
    overflow-x: auto;
  }
  @include until($tablet){
    padding: 1rem;
    background: $primary;
  }
  .propsal-escrow{
    .popper{
      max-width: 33vw;
    }
  }

  .proposals-and-timelocks{
    margin-bottom: 1rem;
  }
  .contract-info{
    position: fixed;
    z-index: 99999;
    bottom: 0;
    left: 1rem;
    background: $primary;
    border-left: 2px solid white;
    border-right: 2px solid white;
    border-top: 2px solid white;
    border-top-left-radius: 5px;
    border-top-right-radius: 5px;
    padding: 1rem;
  }
  .top-piece{
    @include until($tablet){
      flex-direction: column-reverse;
      display: flex;
    }
  }
  .top-section{
    background: $primary;
    padding-bottom: 8rem;
    .hero-body{
      .title{
        font-size: 3.25rem;
      }
      .subtitle{
        font-size: 1.875rem;
      }
    }
  }
  .info-box{
    margin-top: -6rem;
    margin-bottom: 6rem;
    &.loading{
      padding: 3rem 0;
    }
  }
}

</style>
